import type { SetUserSkillsRequest, UserSkill } from '@compass/client/types/user';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { userService } from '@/services/api';

const queryKeys = {
  userSkills: {
    all: ['compass', 'userSkills'] as const,
    current: () => [...queryKeys.userSkills.all, 'current'] as const,
    byUser: (organizationId: string, userId: string) =>
      [...queryKeys.userSkills.all, organizationId, userId] as const,
  },
};

/**
 * Fetch the authenticated user's skills across all organizations.
 * Read-only (owned scope) — users cannot edit their own skills.
 */
export const useCurrentUserSkills = () =>
  useQuery({
    queryKey: queryKeys.userSkills.current(),
    queryFn: () => userService.getCurrentUserSkills({}),
    staleTime: 5 * 60 * 1000,
  });

/**
 * Fetch a user's skills within an organization (org-admin read).
 */
export const useUserSkills = (organizationId?: string, userId?: string) =>
  useQuery({
    queryKey: queryKeys.userSkills.byUser(organizationId || '', userId || ''),
    queryFn: () => {
      if (!organizationId || !userId) {
        throw new Error('Organization ID and User ID are required');
      }
      return userService.getUserSkills({ organization_id: organizationId, user_id: userId });
    },
    enabled: !!organizationId && !!userId,
    staleTime: 2 * 60 * 1000,
  });

/**
 * Fetches skills for multiple users within an organization in parallel.
 * There is no bulk get-skills-by-user-ids endpoint, so this fans out one
 * getUserSkills call per user (mirrors the useTeamsByIds pattern in useTeams.ts).
 */
export const useUsersSkillsMap = (organizationId: string | undefined, userIds: string[]) => {
  const queries = useQueries({
    queries: userIds.map(userId => ({
      queryKey: queryKeys.userSkills.byUser(organizationId || '', userId),
      queryFn: () =>
        userService.getUserSkills({ organization_id: organizationId, user_id: userId }),
      enabled: !!organizationId && !!userId,
      staleTime: 2 * 60 * 1000,
    })),
  });

  const skillsMap = useMemo(() => {
    const map = new Map<string, UserSkill[]>();
    userIds.forEach((userId, index) => {
      map.set(userId, queries[index]?.data?.skills || []);
    });
    return map;
  }, [queries, userIds]);

  const isLoading = queries.some(q => q.isLoading);

  return { skillsMap, isLoading };
};

/**
 * Replace a user's full skill set within an organization (org-admin).
 * SetUserSkills has full-replace semantics: the request must contain the
 * complete desired set, not an incremental add.
 */
export const useSetUserSkills = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: SetUserSkillsRequest) => userService.setUserSkills(request),
    onSuccess: (_data, variables) => {
      const { organization_id, user_id } = variables;
      if (organization_id && user_id) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.userSkills.byUser(organization_id, user_id),
        });
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.userSkills.current() });
    },
  });
};
